import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { axios } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

export default function OgrenciDenemeAnalizPage() {
  const location = useLocation();

  // Filtre ve Seçim State'leri
  const [ogrenciler, setOgrenciler] = useState([]);
  const [siniflar, setSiniflar] = useState([]);
  const [loadingOgrenciler, setLoadingOgrenciler] = useState(true);
  const [seciliSinifId, setSeciliSinifId] = useState('');
  const [ogrenciArama, setOgrenciArama] = useState('');
  const [seciliOgrenci, setSeciliOgrenci] = useState(null);

  // Öğrenciye Ait Denemeler ve Seçimler
  const [ogrenciDenemeleri, setOgrenciDenemeleri] = useState([]);
  const [seciliDenemeIds, setSeciliDenemeIds] = useState([]);
  const [loadingDenemeler, setLoadingDenemeler] = useState(false);

  // Teknik Analiz Sonuç Verisi
  const [analizSonuc, setAnalizSonuc] = useState(null);
  const [hesaplaniyor, setHesaplaniyor] = useState(false);

  // UI Görünüm ve Tab State'leri
  const [seciliDersFiltre, setSeciliDersFiltre] = useState('TÜMÜ');
  const [konuArama, setKonuArama] = useState('');
  const [oncelikFiltre, setOncelikFiltre] = useState('tum');
  const [genisletilmisKonu, setGenisletilmisKonu] = useState(null);
  const [aktifTab, setAktifTab] = useState('konular'); // 'konular' | 'grafikler' | 'denemeMatrisi'
  const [showStudentPicker, setShowStudentPicker] = useState(false);
  const [showDenemePicker, setShowDenemePicker] = useState(false);

  // Ders İkonu Yardımcısı
  const getDersIcon = (dersAdi = '') => {
    const d = dersAdi.toLowerCase();
    if (d.includes('mat') || d.includes('geometri')) return '📐';
    if (d.includes('türkçe') || d.includes('turkce') || d.includes('edebiyat')) return '📖';
    if (d.includes('fizik')) return '⚡';
    if (d.includes('kimya')) return '🧪';
    if (d.includes('biyo')) return '🧬';
    if (d.includes('fen')) return '🔬';
    if (d.includes('tarih')) return '🏛️';
    if (d.includes('coğrafya') || d.includes('cografya')) return '🗺️';
    if (d.includes('felsefe')) return '🧠';
    if (d.includes('din')) return '🕊️';
    if (d.includes('ingilizce') || d.includes('dil')) return '🌐';
    if (d.includes('sosyal')) return '🌍';
    return '📝';
  };

  // 1. Sınıfları ve Öğrencileri Yükle
  const loadOgrenciler = useCallback(async () => {
    setLoadingOgrenciler(true);
    try {
      const [ogrRes, sinifRes] = await Promise.all([
        axios.get(`/denemeler/teknik-analiz/ogrenciler?sinif_id=${seciliSinifId}&arama=${ogrenciArama}`),
        axios.get('/siniflar'),
      ]);
      if (ogrRes.data.success) {
        setOgrenciler(ogrRes.data.data || []);
      }
      if (sinifRes.data.success) {
        setSiniflar(sinifRes.data.data || []);
      }
    } catch {
      toast.error('Öğrenci listesi yüklenemedi');
    } finally {
      setLoadingOgrenciler(false);
    }
  }, [seciliSinifId, ogrenciArama]);

  useEffect(() => {
    loadOgrenciler();
  }, [loadOgrenciler]);

  // URL'den öğrenci ID'si geldiyse doğrudan seç
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlOgrId = params.get('ogrenci_id');
    if (urlOgrId && ogrenciler.length > 0) {
      const found = ogrenciler.find((o) => String(o.ogrenci_id) === String(urlOgrId));
      if (found) {
        handleOgrenciSec(found);
      }
    }
  }, [location.search, ogrenciler]);

  // 2. Öğrenci Seçildiğinde Denemelerini Getir
  const handleOgrenciSec = async (ogr) => {
    setSeciliOgrenci(ogr);
    setShowStudentPicker(false);
    setShowDenemePicker(false);
    setAnalizSonuc(null);
    setLoadingDenemeler(true);
    try {
      const { data } = await axios.get('/denemeler/teknik-analiz/ogrenci-denemeleri', {
        params: {
          ogrenci_id: ogr.ogrenci_id,
          ogrenci_no: ogr.ogrenci_no,
          ad: ogr.ad,
          soyad: ogr.soyad,
        },
      });

      if (data.success && data.data) {
        const denemeler = data.data || [];
        setOgrenciDenemeleri(denemeler);

        // Varsayılan olarak son 5 denemeyi seç (veya varsa tümünü)
        const sonDenemeler = denemeler.slice(-5);
        const defaultIds = sonDenemeler.map((d) => d.deneme_id);
        setSeciliDenemeIds(defaultIds);

        // Otomatik olarak analizi çalıştır
        if (defaultIds.length > 0) {
          calistirTeknikAnaliz(ogr, defaultIds);
        }
      }
    } catch {
      toast.error('Öğrencinin denemeleri alınırken hata oluştu');
    } finally {
      setLoadingDenemeler(false);
    }
  };

  // 3. Teknik Analiz Hesaplamasını Çalıştır
  const calistirTeknikAnaliz = async (ogr = seciliOgrenci, denemeIds = seciliDenemeIds) => {
    if (!ogr) {
      toast.error('Lütfen bir öğrenci seçin');
      return;
    }
    if (!denemeIds || denemeIds.length === 0) {
      toast.error('Lütfen en az bir deneme seçin');
      return;
    }

    setHesaplaniyor(true);
    try {
      const { data } = await axios.post('/denemeler/teknik-analiz/hesapla', {
        ogrenci_id: ogr.ogrenci_id,
        ogrenci_no: ogr.ogrenci_no,
        ad: ogr.ad,
        soyad: ogr.soyad,
        deneme_ids: denemeIds,
      });

      if (data.success) {
        setAnalizSonuc(data.data);
        toast.success('Teknik analiz başarıyla tamamlandı!');
      } else {
        toast.error(data.message || 'Analiz hesaplanamadı');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Analiz sırasında hata oluştu');
    } finally {
      setHesaplaniyor(false);
    }
  };

  // Hızlı Seçim Yardımcıları
  const handleQuickSelect = (count) => {
    if (!ogrenciDenemeleri.length) return;
    let selected = [];
    if (count === 'all') {
      selected = ogrenciDenemeleri.map((d) => d.deneme_id);
    } else {
      selected = ogrenciDenemeleri.slice(-count).map((d) => d.deneme_id);
    }
    setSeciliDenemeIds(selected);
    if (seciliOgrenci && selected.length > 0) {
      calistirTeknikAnaliz(seciliOgrenci, selected);
    }
  };

  const handleDenemeToggle = (id) => {
    setSeciliDenemeIds((prev) => {
      const exists = prev.includes(id);
      return exists ? prev.filter((item) => item !== id) : [...prev, id];
    });
  };

  // Ders Listesi Filtresi
  const dersIsimleri = useMemo(() => {
    if (!analizSonuc?.dersler) return [];
    return Object.keys(analizSonuc.dersler);
  }, [analizSonuc]);

  // Tablo için Filtrelenmiş Konu Listesi
  const filtrelenmisKonular = useMemo(() => {
    if (!analizSonuc?.tumKonular) return [];
    let list = analizSonuc.tumKonular;

    if (seciliDersFiltre !== 'TÜMÜ') {
      list = list.filter((k) => k.dersAdi === seciliDersFiltre);
    }

    if (oncelikFiltre !== 'tum') {
      list = list.filter((k) => k.oncelik === oncelikFiltre);
    }

    if (konuArama.trim()) {
      const q = konuArama.toLowerCase();
      list = list.filter(
        (k) => k.konu.toLowerCase().includes(q) || k.dersAdi.toLowerCase().includes(q)
      );
    }

    return list;
  }, [analizSonuc, seciliDersFiltre, oncelikFiltre, konuArama]);

  // Önerileri Panoya Kopyalama
  const handleCopyOneriler = () => {
    if (!analizSonuc?.akilliOneriler?.length) return;
    const text = analizSonuc.akilliOneriler
      .map((o, idx) => `${idx + 1}. [${o.kategori}] ${o.baslik}\n${o.metin}\n`)
      .join('\n');

    navigator.clipboard.writeText(text);
    toast.success('Öğrenci çalışma önerileri panoya kopyalandı! 📋');
  };

  // Yazdır / PDF İndir
  const handlePrint = () => {
    window.print();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingBottom: 60, color: '#0F1E3D', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }} className="teknik-analiz-container">
      {/* Özel Yazdırma, Kontrast ve Responsive Stilleri */}
      <style>{`
        @media print {
          body { background: white !important; color: black !important; }
          .app-sidebar, .topbar, .btn, .no-print { display: none !important; }
          .page-content { padding: 0 !important; margin: 0 !important; }
          .card, .teknik-card { border: 1px solid #ccc !important; box-shadow: none !important; page-break-inside: avoid; }
        }
        .teknik-analiz-container {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          color: #0F1E3D !important;
          overflow-x: hidden;
        }
        .teknik-analiz-container h1,
        .teknik-analiz-container h2,
        .teknik-analiz-container h3,
        .teknik-analiz-container h4 {
          color: #0F1E3D !important;
        }
        .teknik-analiz-container .form-input,
        .teknik-analiz-container .form-select {
          color: #0F1E3D !important;
          background-color: #FFFFFF !important;
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 8px 12px;
          box-sizing: border-box;
        }
        .teknik-analiz-container .form-input::placeholder {
          color: #94A3B8;
        }
        .teknik-analiz-container .form-label {
          color: #1E293B !important;
          font-weight: 700;
        }
        .teknik-analiz-container .table th {
          color: #1E293B !important;
          font-weight: 800;
          background: #F8FAFC;
        }
        .teknik-analiz-container .table td {
          color: #0F1E3D !important;
        }
        .teknik-card {
          width: 100%;
          box-sizing: border-box;
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 14px;
          padding: 16px 20px;
          box-shadow: 0 2px 10px rgba(15,30,61,0.04);
          transition: transform 0.2s, box-shadow 0.2s;
          color: #0F1E3D !important;
        }
        .command-bar {
          background: #FFFFFF;
          border: 1.5px solid var(--border);
          border-radius: 14px;
          padding: 14px 18px;
          box-shadow: 0 2px 12px rgba(15,30,61,0.05);
          width: 100%;
          box-sizing: border-box;
        }
        .student-select-card {
          padding: 10px 14px;
          border-radius: 10px;
          cursor: pointer;
          border: 1.5px solid var(--border);
          background: var(--bg-card);
          transition: all 0.15s ease;
          display: flex;
          align-items: center;
          gap: 10px;
          box-sizing: border-box;
        }
        .student-select-card:hover {
          border-color: var(--blue);
          background: #F8FAFF;
          transform: translateY(-1px);
        }
        .student-select-card.selected {
          border-color: var(--accent);
          background: rgba(232, 51, 74, 0.06);
        }
        .teknik-kpi {
          border-radius: 12px;
          padding: 14px 18px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          position: relative;
          overflow: hidden;
        }
        .badge-oncelik-yuksek {
          background: rgba(239, 68, 68, 0.12);
          color: #EF4444;
          border: 1px solid rgba(239, 68, 68, 0.25);
          font-weight: 700;
          font-size: 0.75rem;
          padding: 3px 10px;
          border-radius: 99px;
        }
        .badge-oncelik-orta {
          background: rgba(245, 158, 11, 0.12);
          color: #F59E0B;
          border: 1px solid rgba(245, 158, 11, 0.25);
          font-weight: 700;
          font-size: 0.75rem;
          padding: 3px 10px;
          border-radius: 99px;
        }
        .badge-oncelik-guclu {
          background: rgba(16, 185, 129, 0.12);
          color: #10B981;
          border: 1px solid rgba(16, 185, 129, 0.25);
          font-weight: 700;
          font-size: 0.75rem;
          padding: 3px 10px;
          border-radius: 99px;
        }
        .chip-tab {
          padding: 6px 14px;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: var(--bg-elevated);
          color: #1E293B !important;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.82rem;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.15s;
          white-space: nowrap;
        }
        .chip-tab:hover {
          background: #E2E8F0;
        }
        .chip-tab.active {
          background: var(--accent);
          color: white !important;
          border-color: var(--accent);
          box-shadow: 0 2px 8px rgba(232, 51, 74, 0.25);
        }
        .deneme-check-card {
          border: 1px solid var(--border);
          border-radius: 10px;
          padding: 10px 14px;
          cursor: pointer;
          background: var(--bg-base);
          transition: all 0.15s;
          user-select: none;
          color: #0F1E3D !important;
          box-sizing: border-box;
        }
        .deneme-check-card:hover {
          border-color: var(--blue);
          background: #F8FAFF;
        }
        .deneme-check-card.selected {
          border-color: var(--accent);
          background: rgba(232, 51, 74, 0.05);
          color: #0F1E3D !important;
        }
        .table-wrapper {
          width: 100%;
          overflow-x: auto;
          box-sizing: border-box;
          border-radius: 10px;
          scrollbar-width: thin;
        }
      `}</style>

      {/* Başlık ve Aksiyonlar */}
      <div className="section-header animate-fadeInUp" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '1.8rem' }}>📊</span>
            <h2 style={{ fontFamily: 'var(--font-ui)', fontSize: '1.6rem', fontWeight: 800, margin: 0 }}>
              Deneme Teknik Analiz Sistemi
            </h2>
          </div>
          <div className="section-subtitle" style={{ marginTop: 4 }}>
            Öğrencinin deneme sonuçları üzerinden konu bazlı başarı, gelişim trendi ve akıllı çalışma önerileri
          </div>
        </div>

        {analizSonuc && (
          <div style={{ display: 'flex', gap: 10 }} className="no-print">
            <button className="btn btn-secondary" onClick={handleCopyOneriler} title="Çalışma önerilerini panoya kopyala">
              📋 Önerileri Kopyala
            </button>
            <button className="btn btn-primary" onClick={handlePrint} title="Raporu yazdır veya PDF kaydet">
              🖨️ Raporu Yazdır / PDF
            </button>
          </div>
        )}
      </div>

      {/* 1. ULTRA MODERN FİLTRE & KONTROL MERKEZİ (COMMAND DECK) */}
      <div className="command-bar animate-fadeInUp no-print">
        {/* Seçili Öğrenci Varsa: Kompakt Komuta Çubuğu */}
        {seciliOgrenci ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              {/* Sol: Öğrenci Profil Özeti */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    flexShrink: 0,
                    boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
                  }}
                >
                  {seciliOgrenci.ad?.[0]}{seciliOgrenci.soyad?.[0]}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0F1E3D', fontFamily: 'var(--font-ui)' }}>
                      {seciliOgrenci.ad} {seciliOgrenci.soyad}
                    </span>
                    {seciliOgrenci.ogrenci_no && (
                      <span className="badge badge-muted" style={{ fontSize: '0.68rem' }}>No: {seciliOgrenci.ogrenci_no}</span>
                    )}
                    <span className="badge badge-info" style={{ fontSize: '0.68rem' }}>{seciliOgrenci.sinif_adi || 'Sınıfsız'}</span>
                    <span className="badge badge-accent" style={{ fontSize: '0.68rem' }}>{seciliOgrenci.sinav_turu || 'TYT / AYT'}</span>
                    {seciliOgrenci.hedef_net > 0 && (
                      <span style={{ fontSize: '0.75rem', color: '#475569', fontWeight: 600 }}>
                        Hedef: {seciliOgrenci.hedef_net} Net
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#64748B', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>Sistemde Kayıtlı: <strong style={{ color: '#10B981' }}>{ogrenciDenemeleri.length} Deneme</strong></span>
                    <span>•</span>
                    <span>İncelenen: <strong style={{ color: 'var(--accent)' }}>{seciliDenemeIds.length} Deneme</strong></span>
                  </div>
                </div>
              </div>

              {/* Sağ: Hızlı Filtre Butonları ve Aksiyonlar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {/* Hızlı Deneme Filtre Presetleri */}
                <div style={{ display: 'flex', gap: 4, background: 'var(--bg-elevated)', padding: 3, borderRadius: 10 }}>
                  <button
                    className={`chip-tab ${seciliDenemeIds.length === 3 ? 'active' : ''}`}
                    onClick={() => handleQuickSelect(3)}
                    style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                    title="Son 3 denemeyi analiz et"
                  >
                    Son 3
                  </button>
                  <button
                    className={`chip-tab ${seciliDenemeIds.length === 5 ? 'active' : ''}`}
                    onClick={() => handleQuickSelect(5)}
                    style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                    title="Son 5 denemeyi analiz et"
                  >
                    Son 5
                  </button>
                  <button
                    className={`chip-tab ${seciliDenemeIds.length === 10 ? 'active' : ''}`}
                    onClick={() => handleQuickSelect(10)}
                    style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                    title="Son 10 denemeyi analiz et"
                  >
                    Son 10
                  </button>
                  <button
                    className={`chip-tab ${seciliDenemeIds.length === ogrenciDenemeleri.length && ogrenciDenemeleri.length > 0 ? 'active' : ''}`}
                    onClick={() => handleQuickSelect('all')}
                    style={{ padding: '4px 10px', fontSize: '0.78rem' }}
                    title="Tüm denemeleri analiz et"
                  >
                    Tümü
                  </button>
                </div>

                {/* Denemeleri Özel Seç Butonu */}
                <button
                  className={`btn btn-sm ${showDenemePicker ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => {
                    setShowDenemePicker(!showDenemePicker);
                    if (showStudentPicker) setShowStudentPicker(false);
                  }}
                  style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                >
                  📑 Denemeler ({seciliDenemeIds.length}) {showDenemePicker ? '▲' : '▼'}
                </button>

                {/* Öğrenci Değiştir Butonu */}
                <button
                  className={`btn btn-sm ${showStudentPicker ? 'btn-blue' : 'btn-secondary'}`}
                  onClick={() => {
                    setShowStudentPicker(!showStudentPicker);
                    if (showDenemePicker) setShowDenemePicker(false);
                  }}
                  style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                >
                  👤 Öğrenci Değiştir {showStudentPicker ? '▲' : '▼'}
                </button>

                {/* Analizi Yenile Butonu */}
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => calistirTeknikAnaliz()}
                  disabled={hesaplaniyor || seciliDenemeIds.length === 0}
                  style={{ fontSize: '0.82rem', padding: '6px 14px', fontWeight: 700 }}
                >
                  {hesaplaniyor ? '⏳ Hesaplanıyor...' : '⚡ Analizi Yenile'}
                </button>
              </div>
            </div>

            {/* AÇILIR ÇEKMECE 1: ÖĞRENCİ SEÇİCİ */}
            {showStudentPicker && (
              <div style={{ paddingTop: 12, borderTop: '1px solid var(--border)', animation: 'fadeIn 0.2s ease' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 240px) 1fr', gap: 10, marginBottom: 10 }}>
                  <select
                    className="form-select"
                    value={seciliSinifId}
                    onChange={(e) => setSeciliSinifId(e.target.value)}
                    style={{ fontSize: '0.85rem' }}
                  >
                    <option value="">✓ Tüm Sınıflar</option>
                    {siniflar.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.ad} ({s.seviye}. Sınıf)
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="🔍 Öğrenci adı, soyadı veya okul numarasıyla ara..."
                    value={ogrenciArama}
                    onChange={(e) => setOgrenciArama(e.target.value)}
                    style={{ fontSize: '0.85rem' }}
                  />
                </div>

                <div style={{ maxHeight: 220, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, paddingRight: 4 }}>
                  {loadingOgrenciler ? (
                    <div style={{ color: '#64748B', fontSize: '0.85rem', padding: 8 }}>Öğrenciler listeleniyor...</div>
                  ) : ogrenciler.length === 0 ? (
                    <div style={{ color: '#64748B', fontSize: '0.85rem', padding: 8 }}>Eşleşen öğrenci bulunamadı.</div>
                  ) : (
                    ogrenciler.map((ogr) => {
                      const isSelected = seciliOgrenci?.ogrenci_id === ogr.ogrenci_id;
                      return (
                        <div
                          key={ogr.ogrenci_id}
                          className={`student-select-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleOgrenciSec(ogr)}
                        >
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              background: isSelected ? 'var(--accent)' : 'var(--bg-elevated)',
                              color: isSelected ? 'white' : '#0F1E3D',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              flexShrink: 0,
                            }}
                          >
                            {ogr.ad?.[0]}{ogr.soyad?.[0]}
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: '0.84rem', color: isSelected ? 'var(--accent)' : '#0F1E3D', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {ogr.ad} {ogr.soyad}
                            </div>
                            <div style={{ fontSize: '0.72rem', color: '#64748B', display: 'flex', gap: 4 }}>
                              <span>{ogr.sinif_adi || 'Sınıfsız'}</span>
                              <span>•</span>
                              <span style={{ fontWeight: 600, color: '#10B981' }}>{ogr.deneme_sayisi || 0} Deneme</span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* AÇILIR ÇEKMECE 2: DENEME LİSTESİ ÇEKMECESİ */}
            {showDenemePicker && (
              <div style={{ paddingTop: 12, borderTop: '1px solid var(--border)', animation: 'fadeIn 0.2s ease' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0F1E3D' }}>
                    Sınavları Tek Tek Seçin ({seciliDenemeIds.length} / {ogrenciDenemeleri.length} seçildi):
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-xs btn-ghost" onClick={() => handleQuickSelect('all')}>Tümünü Seç</button>
                    <button className="btn btn-xs btn-ghost" onClick={() => setSeciliDenemeIds([])}>Tümünü Kaldır</button>
                    <button className="btn btn-xs btn-primary" onClick={() => { setShowDenemePicker(false); calistirTeknikAnaliz(); }}>✓ Uygula & Analiz Et</button>
                  </div>
                </div>

                <div style={{ maxHeight: 220, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 8, paddingRight: 4 }}>
                  {ogrenciDenemeleri.map((deneme) => {
                    const isChecked = seciliDenemeIds.includes(deneme.deneme_id);
                    return (
                      <div
                        key={deneme.deneme_id}
                        className={`deneme-check-card ${isChecked ? 'selected' : ''}`}
                        onClick={() => handleDenemeToggle(deneme.deneme_id)}
                        style={{ padding: '8px 12px' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {}}
                              style={{ cursor: 'pointer', accentColor: 'var(--accent)' }}
                            />
                            <span style={{ fontWeight: 700, fontSize: '0.82rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {deneme.deneme_adi}
                            </span>
                          </div>
                          <span style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--accent)', flexShrink: 0 }}>
                            {deneme.toplam_net} N
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#64748B', marginTop: 4 }}>
                          <span>{deneme.tarih ? new Date(deneme.tarih).toLocaleDateString('tr-TR') : 'Tarihsiz'}</span>
                          <span className="badge badge-muted" style={{ padding: '1px 5px', fontSize: '0.65rem' }}>{deneme.tur}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Henüz Öğrenci Seçilmemişse: Modern Öğrenci Seçim Paneli */
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: '1.2rem' }}>🎯</span>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1rem', color: '#0F1E3D' }}>1. Analiz Edilecek Öğrenciyi Seçin</div>
                <div style={{ fontSize: '0.78rem', color: '#64748B' }}>Kayıtlı ve deneme sınavına girmiş öğrenciler taranıyor</div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 240px) 1fr', gap: 10, marginBottom: 12 }}>
              <select
                className="form-select"
                value={seciliSinifId}
                onChange={(e) => setSeciliSinifId(e.target.value)}
                style={{ fontSize: '0.85rem' }}
              >
                <option value="">✓ Tüm Sınıflar</option>
                {siniflar.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.ad} ({s.seviye}. Sınıf)
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="form-input"
                placeholder="🔍 Öğrenci adı, soyadı veya okul numarası yazarak arayın..."
                value={ogrenciArama}
                onChange={(e) => setOgrenciArama(e.target.value)}
                style={{ fontSize: '0.85rem' }}
              />
            </div>

            <div style={{ maxHeight: 280, overflowY: 'auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 8, paddingRight: 4 }}>
              {loadingOgrenciler ? (
                <div style={{ color: '#64748B', fontSize: '0.85rem', padding: 8 }}>Öğrenciler yükleniyor...</div>
              ) : ogrenciler.length === 0 ? (
                <div style={{ color: '#64748B', fontSize: '0.85rem', padding: 8 }}>Eşleşen öğrenci bulunamadı.</div>
              ) : (
                ogrenciler.map((ogr) => (
                  <div
                    key={ogr.ogrenci_id}
                    className="student-select-card"
                    onClick={() => handleOgrenciSec(ogr)}
                  >
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #2563EB, #1D4ED8)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        flexShrink: 0,
                      }}
                    >
                      {ogr.ad?.[0]}{ogr.soyad?.[0]}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.86rem', color: '#0F1E3D', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ogr.ad} {ogr.soyad}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#64748B', display: 'flex', gap: 4, marginTop: 2 }}>
                        <span>{ogr.sinif_adi || 'Sınıfsız'}</span>
                        <span>•</span>
                        <span style={{ fontWeight: 600, color: '#10B981' }}>{ogr.deneme_sayisi || 0} Deneme</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* 3. TEKNİK ANALİZ SONUÇLARI */}
      {hesaplaniyor && (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>Teknik Analiz Motoru Çalışıyor...</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 4 }}>
            Konu kazanımları, doğru/yanlış oranları ve gelişim trendleri haritalandırılıyor.
          </div>
        </div>
      )}

      {analizSonuc && !hesaplaniyor && (
        <>
          {/* KPI Kartları (Genel İstatistikler) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }} className="animate-fadeInUp">
            {/* İncelenen Deneme */}
            <div className="teknik-card teknik-kpi" style={{ borderLeft: '4px solid #6366F1' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>İNCELENEN DENEME</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, fontFamily: 'var(--font-ui)', color: '#6366F1' }}>
                {analizSonuc.ozet.secilenDenemeSayisi} <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Sınav</span>
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Seçilen filtre bazında
              </div>
            </div>

            {/* Toplam Soru & Doğruluk */}
            <div className="teknik-card teknik-kpi" style={{ borderLeft: '4px solid #10B981' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>TOPLAM SORU & BAŞARI</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, fontFamily: 'var(--font-ui)', color: '#10B981' }}>
                %{analizSonuc.ozet.genelBasariYuzdesi}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                {analizSonuc.ozet.toplamSoru} Soruda {analizSonuc.ozet.toplamDogru} Doğru, {analizSonuc.ozet.toplamYanlis} Yanlış, {analizSonuc.ozet.toplamBos} Boş
              </div>
            </div>

            {/* Ortalama Net */}
            <div className="teknik-card teknik-kpi" style={{ borderLeft: '4px solid #3B82F6' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>ORTALAMA NET</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, fontFamily: 'var(--font-ui)', color: '#3B82F6' }}>
                {analizSonuc.ozet.ortalamaNet}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Ortalama Puan: {analizSonuc.ozet.ortalamaPuan}
              </div>
            </div>

            {/* Net Gelişim Trendi */}
            <div className="teknik-card teknik-kpi" style={{ borderLeft: `4px solid ${analizSonuc.ozet.netGelisimDelta >= 0 ? '#10B981' : '#EF4444'}` }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>NET GELİŞİM İVMESİ</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, fontFamily: 'var(--font-ui)', color: analizSonuc.ozet.netGelisimDelta >= 0 ? '#10B981' : '#EF4444' }}>
                {analizSonuc.ozet.netGelisimDelta >= 0 ? `+${analizSonuc.ozet.netGelisimDelta}` : analizSonuc.ozet.netGelisimDelta}
                <span style={{ fontSize: '1rem', marginLeft: 4 }}>
                  {analizSonuc.ozet.netGelisimDelta >= 0 ? '📈' : '📉'}
                </span>
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                İlk deneme ile son deneme arası
              </div>
            </div>
          </div>

          {/* DERS BAZLI PERFORMANS KARNESİ (DOĞRU, YANLIŞ, BOŞ, TOPLAM SORU, NET) */}
          {analizSonuc.dersler && Object.keys(analizSonuc.dersler).length > 0 && (
            <div className="teknik-card animate-fadeInUp" style={{ borderTop: '4px solid var(--blue)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: '1.3rem' }}>📚</span>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#0F1E3D' }}>
                      Ders Bazlı Performans Karnesi (Soru, Doğru, Yanlış, Boş ve Net Dağılımı)
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#64748B' }}>
                      İncelenen sınavlarda derslerin genel soru çözüm dağılımı (Derse tıklayarak konu tablosunu filtreleyebilirsiniz)
                    </div>
                  </div>
                </div>
                {seciliDersFiltre !== 'TÜMÜ' && (
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => setSeciliDersFiltre('TÜMÜ')}
                    style={{ fontSize: '0.8rem', color: 'var(--accent)', fontWeight: 700 }}
                  >
                    ✕ Filtreyi Kaldır (Tüm Dersler)
                  </button>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                {Object.values(analizSonuc.dersler).map((d) => {
                  const isFiltered = seciliDersFiltre === d.dersAdi;
                  let bColor = '#EF4444';
                  let bBg = 'rgba(239, 68, 68, 0.1)';
                  if (d.basariYuzdesi >= 75) {
                    bColor = '#10B981';
                    bBg = 'rgba(16, 185, 129, 0.1)';
                  } else if (d.basariYuzdesi >= 55) {
                    bColor = '#2563EB';
                    bBg = 'rgba(37, 99, 235, 0.1)';
                  } else if (d.basariYuzdesi >= 40) {
                    bColor = '#F59E0B';
                    bBg = 'rgba(245, 158, 11, 0.1)';
                  }

                  const icon = getDersIcon(d.dersAdi);
                  const ortNet = d.netOrtalama !== undefined
                    ? d.netOrtalama
                    : Number(((d.toplamNet || 0) / (analizSonuc.ozet.secilenDenemeSayisi || 1)).toFixed(2));

                  return (
                    <div
                      key={d.dersAdi}
                      onClick={() => {
                        setSeciliDersFiltre(isFiltered ? 'TÜMÜ' : d.dersAdi);
                        setAktifTab('konular');
                      }}
                      style={{
                        background: isFiltered ? 'rgba(37,99,235,0.08)' : 'var(--bg-base)',
                        border: `1.5px solid ${isFiltered ? 'var(--blue)' : 'var(--border)'}`,
                        borderRadius: 12,
                        padding: '14px 16px',
                        cursor: 'pointer',
                        transition: 'all 0.18s ease',
                        boxShadow: isFiltered ? '0 4px 14px rgba(37,99,235,0.15)' : 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                        boxSizing: 'border-box',
                      }}
                      title={`${d.dersAdi} konularını incelemek için tıklayın`}
                    >
                      {/* Üst: Ders Adı & Başarı % */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: '1.2rem' }}>{icon}</span>
                          <span style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0F1E3D' }}>{d.dersAdi}</span>
                        </div>
                        <span
                          style={{
                            fontSize: '0.78rem',
                            fontWeight: 800,
                            color: bColor,
                            background: bBg,
                            padding: '3px 8px',
                            border: `1px solid ${bColor}40`,
                            borderRadius: 8,
                          }}
                        >
                          %{d.basariYuzdesi}
                        </span>
                      </div>

                      {/* Orta: Doğru / Yanlış / Boş / Toplam Soru */}
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(4, 1fr)',
                          gap: 6,
                          background: 'var(--bg-card)',
                          padding: '8px 10px',
                          borderRadius: 8,
                          border: '1px solid var(--border)',
                          textAlign: 'center',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: '0.68rem', color: '#64748B', fontWeight: 600 }}>TOPLAM</div>
                          <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#0F1E3D' }}>{d.toplamSoru}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.68rem', color: '#10B981', fontWeight: 700 }}>DOĞRU</div>
                          <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#10B981' }}>{d.toplamDogru}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.68rem', color: '#EF4444', fontWeight: 700 }}>YANLIŞ</div>
                          <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#EF4444' }}>{d.toplamYanlis}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.68rem', color: '#64748B', fontWeight: 700 }}>BOŞ</div>
                          <div style={{ fontSize: '0.88rem', fontWeight: 800, color: '#64748B' }}>{d.toplamBos}</div>
                        </div>
                      </div>

                      {/* Alt: Net ve İlerleme Çubuğu */}
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.78rem', marginBottom: 4 }}>
                          <span style={{ color: '#64748B' }}>Ortalama Net:</span>
                          <span style={{ fontWeight: 800, color: '#0F1E3D' }}>{ortNet} Net</span>
                        </div>
                        <div style={{ height: 6, width: '100%', background: 'var(--bg-elevated)', borderRadius: 99, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${d.basariYuzdesi}%`, background: bColor, borderRadius: 99 }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 4 ÖZEL ANALİZ LİSTESİ (Vurgulanan Kritik Paneller) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }} className="animate-fadeInUp stagger-1">
            {/* 1. En Çok Yanlış Yapılan 5 Konu */}
            <div className="teknik-card" style={{ borderTop: '4px solid #EF4444' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: '1.2rem' }}>🔴</span>
                <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>En Çok Yanlış Yapılan 5 Konu</div>
              </div>

              {analizSonuc.enCokYanlis5.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Yanlış yapılan konu tespit edilmedi.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {analizSonuc.enCokYanlis5.map((k, idx) => (
                    <div key={idx} style={{ padding: '8px 12px', background: 'var(--bg-base)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{k.konu}</span>
                        <span className="badge badge-danger" style={{ fontSize: '0.75rem' }}>{k.toplamYanlis} Yanlış</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                        <span>{k.dersAdi}</span>
                        <span>{k.toplamSoru} Soruda %{k.basariYuzdesi} Başarı</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 2. Öncelikli Çalışılması Gereken Konular */}
            <div className="teknik-card" style={{ borderTop: '4px solid #F59E0B' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>Öncelikli Çalışılacak Konular</div>
              </div>

              {analizSonuc.oncelikliKonular.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Acil öncelikli konu bulunmuyor.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {analizSonuc.oncelikliKonular.slice(0, 5).map((k, idx) => (
                    <div key={idx} style={{ padding: '8px 12px', background: 'var(--bg-base)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{k.konu}</span>
                        <span className="badge-oncelik-yuksek">Kritik</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                        <span>{k.dersAdi}</span>
                        <span>{k.toplamYanlis} Y, {k.toplamBos} B (%{k.basariYuzdesi})</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 3. En Fazla Gelişim Gösterilen Konular */}
            <div className="teknik-card" style={{ borderTop: '4px solid #10B981' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: '1.2rem' }}>🚀</span>
                <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>En Fazla Gelişim Gösterilenler</div>
              </div>

              {analizSonuc.enFazlaGelisim.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Henüz karşılaştırılabilir gelişim verisi yok.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {analizSonuc.enFazlaGelisim.slice(0, 5).map((k, idx) => (
                    <div key={idx} style={{ padding: '8px 12px', background: 'var(--bg-base)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{k.konu}</span>
                        <span className="badge-oncelik-guclu">+{k.delta}% İvme</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                        <span>{k.dersAdi}</span>
                        <span>Genel Başarı: %{k.basariYuzdesi}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 4. Başarılı Olunan Güçlü Konular */}
            <div className="teknik-card" style={{ borderTop: '4px solid #3B82F6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: '1.2rem' }}>🏆</span>
                <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>Başarılı Olunan Güçlü Konular</div>
              </div>

              {analizSonuc.gucluKonular.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Güçlü konu listesi oluşmadı.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {analizSonuc.gucluKonular.slice(0, 5).map((k, idx) => (
                    <div key={idx} style={{ padding: '8px 12px', background: 'var(--bg-base)', borderRadius: 8, border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{k.konu}</span>
                        <span className="badge badge-success">%{k.basariYuzdesi}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                        <span>{k.dersAdi}</span>
                        <span>{k.toplamDogru} / {k.toplamSoru} Doğru</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* AKILLI ÇALIŞMA & REHBERLİK ÖNERİLERİ */}
          {analizSonuc.akilliOneriler.length > 0 && (
            <div className="teknik-card animate-fadeInUp" style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.05), rgba(99,102,241,0.03))' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: '1.4rem' }}>💡</span>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '1.1rem', fontFamily: 'var(--font-ui)' }}>
                      Öğrenciye Özel Akıllı Çalışma & Rehberlik Önerileri
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      Analiz edilen sınav performansına göre üretilmiş stratejik aksiyon planı
                    </div>
                  </div>
                </div>

                <button className="btn btn-sm btn-outline no-print" onClick={handleCopyOneriler}>
                  📋 Panoya Kopyala
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14 }}>
                {analizSonuc.akilliOneriler.map((oneri, idx) => {
                  const borderColors = {
                    danger: '#EF4444',
                    warning: '#F59E0B',
                    success: '#10B981',
                    info: '#3B82F6',
                  };
                  return (
                    <div
                      key={idx}
                      style={{
                        background: 'var(--bg-card)',
                        border: '1px solid var(--border)',
                        borderLeft: `4px solid ${borderColors[oneri.tip] || '#6366F1'}`,
                        borderRadius: 12,
                        padding: '14px 18px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', color: borderColors[oneri.tip] || 'var(--accent)' }}>
                          {oneri.kategori}
                        </span>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 6 }}>
                        {oneri.baslik}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        {oneri.metin}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 4. DERS VE KONU BAZLI DETAYLI TEKNİK ANALİZ TABLOSU */}
          <div className="teknik-card animate-fadeInUp">
            {/* Üst Sekmeler */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 14, marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className={`chip-tab ${aktifTab === 'konular' ? 'active' : ''}`}
                  onClick={() => setAktifTab('konular')}
                >
                  📚 Konu Analiz Tablosu ({filtrelenmisKonular.length})
                </button>
                <button
                  className={`chip-tab ${aktifTab === 'grafikler' ? 'active' : ''}`}
                  onClick={() => setAktifTab('grafikler')}
                >
                  📈 Gelişim & Net Grafikleri
                </button>
                <button
                  className={`chip-tab ${aktifTab === 'denemeMatrisi' ? 'active' : ''}`}
                  onClick={() => setAktifTab('denemeMatrisi')}
                >
                  🗂️ Deneme Karşılaştırma Matrisi
                </button>
              </div>
            </div>

            {/* TAB 1: KONULAR TABLOSU */}
            {aktifTab === 'konular' && (
              <div>
                {/* Ders Filtre Sekmeleri */}
                <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 10, marginBottom: 14, scrollbarWidth: 'thin' }}>
                  <button
                    className={`chip-tab ${seciliDersFiltre === 'TÜMÜ' ? 'active' : ''}`}
                    onClick={() => setSeciliDersFiltre('TÜMÜ')}
                  >
                    Tüm Dersler ({analizSonuc.tumKonular.length})
                  </button>
                  {dersIsimleri.map((dersAdi) => {
                    const dInfo = analizSonuc.dersler?.[dersAdi];
                    return (
                      <button
                        key={dersAdi}
                        className={`chip-tab ${seciliDersFiltre === dersAdi ? 'active' : ''}`}
                        onClick={() => setSeciliDersFiltre(dersAdi)}
                      >
                        <span>{getDersIcon(dersAdi)}</span>
                        <span>{dersAdi}</span>
                        {dInfo && (
                          <span style={{ fontSize: '0.72rem', opacity: 0.88, fontWeight: 700, marginLeft: 2 }}>
                            ({dInfo.toplamDogru}D / {dInfo.toplamYanlis}Y • %{dInfo.basariYuzdesi})
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Arama ve Öncelik Filtresi */}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="🔍 Konu adına göre ara..."
                    value={konuArama}
                    onChange={(e) => setKonuArama(e.target.value)}
                    style={{ flex: 1, minWidth: 220 }}
                  />

                  <select
                    className="form-select"
                    value={oncelikFiltre}
                    onChange={(e) => setOncelikFiltre(e.target.value)}
                    style={{ minWidth: 180 }}
                  >
                    <option value="tum">✓ Tüm Öncelik Seviyeleri</option>
                    <option value="yuksek">🔴 Sadece Kritik / Yüksek Öncelik</option>
                    <option value="orta">🟡 Tekrar Edilmeli</option>
                    <option value="guclu">🟢 Güçlü Konular</option>
                  </select>
                </div>

                {/* Konular Tablosu */}
                <div className="table-wrapper">
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: '30%' }}>Ders & Konu</th>
                        <th style={{ textAlign: 'center' }}>Soru</th>
                        <th style={{ textAlign: 'center' }}>Doğru</th>
                        <th style={{ textAlign: 'center' }}>Yanlış</th>
                        <th style={{ textAlign: 'center' }}>Boş</th>
                        <th style={{ width: '22%' }}>Başarı Oranı</th>
                        <th style={{ textAlign: 'center' }}>Trend</th>
                        <th style={{ textAlign: 'center' }}>Öncelik</th>
                        <th style={{ textAlign: 'center' }}>Detay</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtrelenmisKonular.length === 0 ? (
                        <tr>
                          <td colSpan={9} style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
                            Filtreye uygun konu bulunamadı.
                          </td>
                        </tr>
                      ) : (
                        filtrelenmisKonular.map((k, i) => {
                          const isExpanded = genisletilmisKonu === k.konu;
                          let progressColor = '#EF4444';
                          if (k.basariYuzdesi >= 75) progressColor = '#10B981';
                          else if (k.basariYuzdesi >= 55) progressColor = '#3B82F6';
                          else if (k.basariYuzdesi >= 40) progressColor = '#F59E0B';

                          return (
                            <React.Fragment key={i}>
                              <tr
                                onClick={() => setGenisletilmisKonu(isExpanded ? null : k.konu)}
                                style={{ cursor: 'pointer', background: isExpanded ? 'rgba(99, 102, 241, 0.04)' : undefined }}
                              >
                                <td>
                                  <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{k.konu}</div>
                                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{k.dersAdi}</div>
                                </td>
                                <td style={{ textAlign: 'center', fontWeight: 600 }}>{k.toplamSoru}</td>
                                <td style={{ textAlign: 'center', color: '#10B981', fontWeight: 700 }}>{k.toplamDogru}</td>
                                <td style={{ textAlign: 'center', color: '#EF4444', fontWeight: 700 }}>{k.toplamYanlis}</td>
                                <td style={{ textAlign: 'center', color: '#94A3B8', fontWeight: 600 }}>{k.toplamBos}</td>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div style={{ flex: 1, height: 8, background: 'var(--bg-elevated)', borderRadius: 99, overflow: 'hidden' }}>
                                      <div style={{ width: `${k.basariYuzdesi}%`, height: '100%', background: progressColor, borderRadius: 99 }} />
                                    </div>
                                    <span style={{ fontSize: '0.8rem', fontWeight: 700, minWidth: 36 }}>%{k.basariYuzdesi}</span>
                                  </div>
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  {k.trend === 'gelisme' && <span title="Son denemelerde gelişim var" style={{ fontSize: '1.1rem' }}>📈</span>}
                                  {k.trend === 'gerileme' && <span title="Son denemelerde gerileme var" style={{ fontSize: '1.1rem' }}>📉</span>}
                                  {k.trend === 'dengeli' && <span title="İstikrarlı seyrediyor" style={{ fontSize: '1.1rem' }}>➡️</span>}
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  {k.oncelik === 'yuksek' && <span className="badge-oncelik-yuksek">Kritik</span>}
                                  {k.oncelik === 'orta' && <span className="badge-oncelik-orta">Tekrar</span>}
                                  {k.oncelik === 'guclu' && <span className="badge-oncelik-guclu">Güçlü</span>}
                                </td>
                                <td style={{ textAlign: 'center' }}>
                                  <button className="btn btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: '0.8rem' }}>
                                    {isExpanded ? '▲ Kapat' : '▼ Açıklama'}
                                  </button>
                                </td>
                              </tr>

                              {/* Genişletilmiş Akıllı Açıklama Satırı */}
                              {isExpanded && (
                                <tr>
                                  <td colSpan={9} style={{ background: 'var(--bg-base)', padding: '16px 20px', borderLeft: `4px solid ${progressColor}` }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                        <span style={{ fontSize: '1.3rem' }}>🧠</span>
                                        <div>
                                          <div style={{ fontWeight: 800, fontSize: '0.9rem', marginBottom: 2, color: '#0F1E3D' }}>Akıllı Teknik Değerlendirme:</div>
                                          <div style={{ fontSize: '0.88rem', color: '#0F1E3D', lineHeight: 1.6, fontWeight: 500 }}>
                                            "{k.akilliYorum}"
                                          </div>
                                        </div>
                                      </div>

                                      {/* Deneme Bazlı Soru Dağılımı */}
                                      {k.denemeGecmisi && k.denemeGecmisi.length > 0 && (
                                        <div style={{ marginTop: 8 }}>
                                          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                                            Sınav Bazlı Soru Dağılımı:
                                          </div>
                                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            {k.denemeGecmisi.map((g, gIdx) => (
                                              <div
                                                key={gIdx}
                                                style={{
                                                  padding: '6px 12px',
                                                  background: 'var(--bg-card)',
                                                  border: '1px solid var(--border)',
                                                  borderRadius: 8,
                                                  fontSize: '0.78rem',
                                                }}
                                              >
                                                <div style={{ fontWeight: 600, color: '#0F1E3D' }}>{g.denemeAdi}</div>
                                                <div style={{ color: '#64748B', fontSize: '0.72rem', marginTop: 2 }}>
                                                  {g.soru} Soru: <strong style={{ color: '#10B981' }}>{g.dogru}D</strong> / <strong style={{ color: '#EF4444' }}>{g.yanlis}Y</strong> / <span style={{ color: '#94A3B8' }}>{g.bos}B</span> (%{g.basari})
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 2: GELİŞİM & NET GRAFİKLERİ */}
            {aktifTab === 'grafikler' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                {/* Net Trend Grafiği */}
                <div style={{ background: 'var(--bg-base)', padding: 20, borderRadius: 14, border: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4, color: '#0F1E3D' }}>
                    📈 Denemeler Arası Toplam Net Değişim Grafiği
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: 16 }}>
                    Seçilen sınavların kronolojik sırasına göre toplam net ve puan eğrisi
                  </div>

                  <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                      <AreaChart data={analizSonuc.trendGrafik} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorNet" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#6366F1" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,30,61,0.08)" />
                        <XAxis dataKey="tarih" stroke="#64748B" fontSize={12} />
                        <YAxis stroke="#64748B" fontSize={12} />
                        <RechartsTooltip contentStyle={{ background: '#FFFFFF', borderRadius: 8, border: '1px solid #CBD5E1', color: '#0F1E3D', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} itemStyle={{ color: '#0F1E3D' }} labelStyle={{ color: '#0F1E3D', fontWeight: 'bold' }} />
                        <Area type="monotone" dataKey="toplamNet" name="Toplam Net" stroke="#6366F1" strokeWidth={3} fillOpacity={1} fill="url(#colorNet)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Ders Dağılım Çubuk Grafiği */}
                <div style={{ background: 'var(--bg-base)', padding: 20, borderRadius: 14, border: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4, color: '#0F1E3D' }}>
                    📊 Ders Bazlı Doğru, Yanlış ve Boş Soru Dağılımı
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#64748B', marginBottom: 16 }}>
                    Derslerin toplam soru çözüm performansının karşılaştırması
                  </div>

                  <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                      <BarChart
                        data={Object.values(analizSonuc.dersler).map((d) => ({
                          ders: d.dersAdi,
                          Dogru: d.toplamDogru,
                          Yanlis: d.toplamYanlis,
                          Bos: d.toplamBos,
                        }))}
                        margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,30,61,0.08)" />
                        <XAxis dataKey="ders" stroke="#64748B" fontSize={12} />
                        <YAxis stroke="#64748B" fontSize={12} />
                        <RechartsTooltip contentStyle={{ background: '#FFFFFF', borderRadius: 8, border: '1px solid #CBD5E1', color: '#0F1E3D', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} itemStyle={{ color: '#0F1E3D' }} labelStyle={{ color: '#0F1E3D', fontWeight: 'bold' }} />
                        <Legend wrapperStyle={{ color: '#0F1E3D' }} />
                        <Bar dataKey="Dogru" name="Doğru" fill="#10B981" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Yanlis" name="Yanlış" fill="#EF4444" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Bos" name="Boş" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 3: DENEME KARŞILAŞTIRMA MATRİSİ */}
            {aktifTab === 'denemeMatrisi' && (
              <div className="table-wrapper">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Deneme Adı</th>
                      <th>Tarih</th>
                      <th>Tür</th>
                      <th style={{ textAlign: 'center' }}>Sıralama</th>
                      <th style={{ textAlign: 'right' }}>Puan</th>
                      <th style={{ textAlign: 'right' }}>Toplam Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analizSonuc.secilenDenemeler.map((e, idx) => (
                      <tr key={idx}>
                        <td style={{ fontWeight: 700 }}>{e.deneme_adi}</td>
                        <td style={{ color: 'var(--text-muted)' }}>
                          {e.tarih ? new Date(e.tarih).toLocaleDateString('tr-TR') : '—'}
                        </td>
                        <td><span className="badge badge-muted">{e.tur}</span></td>
                        <td style={{ textAlign: 'center' }}>{e.siralama ? `${e.siralama}.` : '—'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{e.puan}</td>
                        <td style={{ textAlign: 'right', fontWeight: 800, color: 'var(--accent)', fontSize: '1rem' }}>
                          {e.toplam_net} Net
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
